import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import './Penalties.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import dropdownIcon from '../../../../assets/Admin/Accordion/dropdown-icon.svg';
import { TableSkeleton } from '../../SkeletonLoader/SkeletonLoader';
import { motion, AnimatePresence } from 'framer-motion';

const TeacherAccordian = ({ canManageFeeSettings = () => true }) => {
  const [expanded, setExpanded] = useState();
  const [data, setData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [isEditingActive, setIsEditingActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0); // 0 for Academic, 1 for Creative Arts

  // Tab configuration
  const tabs = [
    {
      name: 'Academic',
      levels: ['elementary', 'high_school', 'middle_school', 'university'],
      displayNames: {
        elementary: 'Elementary',
        high_school: 'High School',
        middle_school: 'Middle School',
        university: 'University',
      },
    },
    {
      name: 'Creative Arts',
      levels: ['beginner', 'intermediate', 'professional'],
      displayNames: {
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        professional: 'Professional',
      },
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setExpanded(null); // Reset expanded accordion when changing tabs
    fetchData(tabs[newValue]);
  };

  // Fetch data when component mounts or tab changes
  useEffect(() => {
    fetchData(tabs[tabValue]);
  }, [tabValue]);

  // Function to fetch data based on current tab
  const fetchData = async tabConfig => {
    try {
      setLoading(true);

      // Initialize data structure
      const schoolLevelData = [];

      // Fetch data for each school level in the current tab
      for (const level of tabConfig.levels) {
        const response = await axiosInstance.get(`/admin/penalty-charges?school_level=${level}`);

        // Map the API data to our format - now accessing results array
        const sessions = response.data.results.map(item => ({
          type: formatSessionTypeName(item.session_type),
          sessionType: item.session_type,
          deduction: parseInt(item.penalty_charge),
          id: item.id,
        }));

        // Sort sessions to maintain consistent order
        sessions.sort((a, b) => {
          const order = {
            one_on_one: 1,
            group: 2,
            prime: 3,
          };
          return order[a.sessionType] - order[b.sessionType];
        });

        // Create a formatted entry for this school level
        schoolLevelData.push({
          name: tabConfig.displayNames[level],
          schoolLevel: level,
          sessions: sessions,
        });
      }

      setData(schoolLevelData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching penalty charges:', error);
      setLoading(false);
    }
  };

  // Format session type to be more readable
  const formatSessionTypeName = sessionType => {
    switch (sessionType) {
      case 'one_on_one':
        return 'One on One Sessions';
      case 'group':
        return 'Group Sessions';
      case 'prime':
        return 'Prime Sessions';
      default:
        return sessionType.charAt(0).toUpperCase() + sessionType.slice(1).replace(/_/g, ' ');
    }
  };

  const handleAccordionClick = categoryName => {
    setExpanded(expanded === categoryName ? null : categoryName);
  };

  const handleEdit = (categoryIndex, sessionIndex) => {
    if (!canManageFeeSettings()) {
      toast.dismiss();
      toast.error("You don't have permission to manage fee settings");
      return;
    }
    setEditingCell(`${categoryIndex}-${sessionIndex}`);
    setIsEditingActive(true);
  };

  const handleSave = async (categoryIndex, sessionIndex) => {
    if (!canManageFeeSettings()) {
      toast.dismiss();
      toast.error("You don't have permission to manage fee settings");
      return;
    }

    try {
      const category = data[categoryIndex];
      const session = category.sessions[sessionIndex];

      // Prepare PATCH payload using the stored original values
      const payload = {
        user_type: 'teacher',
        session_type: session.sessionType,
        school_level: category.schoolLevel,
        penalty_charge: session.deduction,
      };

      // Make PATCH request
      await axiosInstance.patch(`/admin/penalty-charges/${session.id}`, payload);

      // Reset editing state
      setEditingCell(null);
      setIsEditingActive(false);

      // Fetch updated data
      fetchData(tabs[tabValue]);
    } catch (error) {
      console.error('Error updating penalty charge:', error);
      // You might want to add error handling UI here
    }
  };

  const handleInputChange = (categoryIndex, sessionIndex, value) => {
    const newData = [...data];
    newData[categoryIndex].sessions[sessionIndex].deduction = value;
    setData(newData);
  };

  const isCreativeArts = tabs[tabValue].name === 'Creative Arts';
  const creativeArtsEquivalence = {
    beginner: 'High School Equivalent',
    intermediate: 'Middle School Equivalent',
    professional: 'University Equivalent',
  };

  return (
    <Box className="penalties-container">
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="penalty charges tabs"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Academic" />
          <Tab label="Creative Arts" />
        </Tabs>
      </Box>

      {/* {isCreativeArts && (
        <Box className="penalties-legend">
          <span className="penalties-legend-item">
            <strong>Beginner</strong> = High School
          </span>
          <span className="penalties-legend-sep">•</span>
          <span className="penalties-legend-item">
            <strong>Intermediate</strong> = Middle School
          </span>
          <span className="penalties-legend-sep">•</span>
          <span className="penalties-legend-item">
            <strong>Professional</strong> = University
          </span>
        </Box>
      )} */}

      {loading ? (
        <Box className="penalties-loading-container" sx={{ p: 2 }}>
          <TableSkeleton />
        </Box>
      ) : (
        data.map((category, categoryIndex) => (
          <Box
            key={categoryIndex}
            className={`penalties-accordion ${expanded === category.name ? 'penalties-accordion-expanded' : ''}`}
          >
            <Box
              className="penalties-accordion-header"
              onClick={() => handleAccordionClick(category.name)}
            >
              <Box className="penalties-title-group">
                <span className="penalties-accordion-title">{category.name}</span>
                {isCreativeArts && (
                  <span className="penalties-equivalence-pill">
                    {creativeArtsEquivalence[category.schoolLevel]}
                  </span>
                )}
              </Box>
              <img
                src={dropdownIcon || '/placeholder.svg'}
                alt="Dropdown Icon"
                className={`penalties-chevron-icon ${expanded === category.name ? 'rotate' : ''}`}
              />
            </Box>

            <AnimatePresence initial={false}>
              {expanded === category.name && (
                <motion.div
                  key={`penalties-content-${categoryIndex}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box className="penalties-table-wrapper">
                    <table className="penalties-fee-table">
                      <thead>
                        <tr>
                          <th className="penalties-table-header penalties-session-type-header">
                            Type of Session
                          </th>
                          <th
                            className="penalties-table-header penalties-deduction-header"
                            colSpan="1"
                          >
                            Deduction (within 8 hours)
                          </th>
                          <th className="penalties-table-header penalties-actions-header">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.sessions.map((session, sessionIndex) => (
                          <tr
                            key={sessionIndex}
                            className={`penalties-table-row ${sessionIndex % 2 === 1 ? 'penalties-alternate-row' : ''}`}
                          >
                            <td className="penalties-table-cell penalties-session-type-cell">
                              {session.type}
                            </td>
                            <td className="penalties-table-cell penalties-deduction-cell">
                              {editingCell === `${categoryIndex}-${sessionIndex}` ? (
                                <input
                                  className="penalties-input-field"
                                  type="number"
                                  value={session.deduction}
                                  onChange={e =>
                                    handleInputChange(categoryIndex, sessionIndex, e.target.value)
                                  }
                                />
                              ) : (
                                `${session.deduction}%`
                              )}
                            </td>
                            <td className="penalties-table-cell penalties-actions-cell">
                              {editingCell === `${categoryIndex}-${sessionIndex}` ? (
                                <button
                                  className="penalties-save-button"
                                  onClick={() => handleSave(categoryIndex, sessionIndex)}
                                >
                                  Save
                                </button>
                              ) : (
                                <button
                                  className={`penalties-edit-button ${
                                    isEditingActive &&
                                    editingCell !== `${categoryIndex}-${sessionIndex}`
                                      ? 'disabled'
                                      : ''
                                  }`}
                                  onClick={() => handleEdit(categoryIndex, sessionIndex)}
                                  disabled={
                                    isEditingActive &&
                                    editingCell !== `${categoryIndex}-${sessionIndex}`
                                  }
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        ))
      )}
    </Box>
  );
};

export default TeacherAccordian;
